/** Activity Log Models for Audit Logging */
import type { ActivityEvent } from '../services/activity-tracker.service';

/** A single recorded activity detail row. Matches the `activityDetails` schema expected by POST /api/ActivityLogs. */
export interface ActivityLogDetail {
  stepName: string;
  stepDescription: string;
  status: 'Success' | 'Failed' | 'InProgress';
  sequenceNo: number;
  startTime: string;
  endTime: string;
  durationMs: number;
  requestPayload: string | null;
  responsePayload: string | null;
  errorMessage: string | null;
}

/** Request model for creating an activity log entry */
export interface CreateActivityLogRequest {
  /** Event type code (e.g., "LOGIN", "LOGOUT", "CREATE", "UPDATE", "DELETE") */
  eventTypeCode: string;

  /** Module/feature name where the activity occurred */
  moduleName: string;

  /** Type of entity being acted upon */
  entityTypeName: string;

  /** Human-readable description with {UserName} placeholder that gets replaced by actual username */
  actionTemplate: string;

  /** Optional: ID of the affected entity (user ID, record ID, etc.) */
  entityId?: string;

  /** Optional: IP address of the client making the request */
  ipAddress?: string;

  /** Optional: User agent string from the client browser */
  userAgent?: string;

  /** Optional: JSON string representing the state before the action (for UPDATE operations) */
  oldValues?: string;

  /** Optional: JSON string representing the new/current state (for CREATE/UPDATE operations) */
  newValues?: string;

  /** Optional UTC timestamp to store as the activity log created date */
  createdDate?: string;

  /** Optional: Ordered list of activity detail rows. Only populated for SIMULATION event type logs. */
  activityDetails?: ActivityLogDetail[];
}

/** Event type codes matching the ActivityEventType table in the database. */
export enum ActivityEventType {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  ACTIVATE = 'ACTIVATE',
  DEACTIVATE = 'DEACTIVATE',
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  SIMULATION = 'SIMULATION',
  WINDOW_CLOSE = 'WINDOW_CLOSE',
  CLONE = 'CLONE',
  ARCHIVE = 'ARCHIVE',
  REQUEST = 'REQUEST',
  APPROVE = 'APPROVE',
  REJECT = 'REJECT',
  EDIT = 'EDIT',
  VIEW = 'VIEW',
  DOWNLOAD = 'DOWNLOAD',
}

/** Module names for different areas of the application */
export enum ActivityModule {
  ASSESSMENT = 'Assessment',
  ADMIN = 'Admin',
  AUDIT = 'Audit',
  TEMPLATE_DESIGNER = 'Template Designer',
}

/** Entity types that can be acted upon */
export enum ActivityEntityType {
  USER = 'User',
  ROLE = 'Role',
  PERMISSION = 'Permission',
  CLIENT = 'Client',
  TEMPLATE = 'Template',
  AUDIT = 'Audit',
  CLIENT_TEMPLATE_CONFIGURATION = 'ClientTemplateConfiguration',
  GLOBAL_VARIABLE = 'GlobalVariable',
  APPOINTMENT = 'Appointment',
  MEMBER_INFO = 'MemberInfo',
  ASSESSMENT = 'Assessment',
  PROVIDER_INFO = 'ProviderInfo',
  DASHBOARD = 'Dashboard',
}

/** Helper class for creating activity log requests */
export class ActivityLogHelper {
  /**
   * Create a login activity log request
   *
   * @param userId - User identifier
   * @param userAgent - Browser user agent string
   * @param additionalData - Additional data to include in newValues JSON
   */
  static createLoginLog(
    userId: string,
    userAgent: string,
    additionalData?: Record<string, unknown>
  ): CreateActivityLogRequest {
    return {
      eventTypeCode: ActivityEventType.LOGIN,
      moduleName: ActivityModule.ADMIN,
      entityTypeName: ActivityEntityType.USER,
      actionTemplate: '{UserName} logged in successfully',
      entityId: userId,
      userAgent: userAgent,
      newValues: JSON.stringify({
        loginTime: new Date().toISOString(),
        ...additionalData,
      }),
    };
  }

  /**
   * Create a logout activity log request
   *
   * @param userId - User identifier
   * @param userAgent - Browser user agent string
   * @param isAutoLogout - Whether this is an automatic logout due to inactivity
   * @param additionalData - Additional data to include in newValues JSON
   */
  static createLogoutLog(
    userId: string,
    userAgent: string,
    isAutoLogout = false,
    additionalData?: Record<string, unknown>
  ): CreateActivityLogRequest {
    return {
      eventTypeCode: ActivityEventType.LOGOUT,
      moduleName: ActivityModule.ADMIN,
      entityTypeName: ActivityEntityType.USER,
      actionTemplate: isAutoLogout
        ? '{UserName} was automatically logged out due to inactivity'
        : '{UserName} logged out',
      entityId: userId,
      userAgent: userAgent,
      newValues: JSON.stringify({
        logoutTime: new Date().toISOString(),
        isAutoLogout: isAutoLogout,
        reason: isAutoLogout ? 'Auto logout due to inactivity' : 'User initiated logout',
        ...additionalData,
      }),
    };
  }

  /**
   * Create a window close activity log request
   *
   * @param userId - User identifier
   * @param userAgent - Browser user agent string
   * @param additionalData - Additional data to include in newValues JSON
   */
  static createWindowCloseLog(
    userId: string,
    userAgent: string,
    additionalData?: Record<string, unknown>
  ): CreateActivityLogRequest {
    return {
      eventTypeCode: ActivityEventType.WINDOW_CLOSE,
      moduleName: ActivityModule.ADMIN,
      entityTypeName: ActivityEntityType.USER,
      actionTemplate: '{UserName} closed the browser window/tab',
      entityId: userId,
      userAgent: userAgent,
      newValues: JSON.stringify({
        closeTime: new Date().toISOString(),
        reason: 'Window closed by user',
        ...additionalData,
      }),
    };
  }

  /**
   * Create a single activity log request from an `ActivityEvent`. Used for NP per-action POSTs — each event maps to one
   * `POST /api/ActivityLogs` call.
   *
   * @param event - An `ActivityEvent` from `ActivityTrackerService.flushEvents()` or `endSession()`
   */
  static createNpActivityLog(event: ActivityEvent): CreateActivityLogRequest {
    return {
      eventTypeCode: event.eventTypeCode ?? ActivityEventType.EDIT,
      moduleName: event.moduleName ?? ActivityModule.ASSESSMENT,
      entityTypeName: event.entityTypeName ?? ActivityEntityType.CLIENT,
      actionTemplate: event.stepName,
      entityId: event.entityId,
      createdDate: event.recordedAt,
      newValues: JSON.stringify({
        description: event.stepDescription,
        status: event.status,
        ...(event.errorMessage ? { errorMessage: event.errorMessage } : {}),
        ...(event.requestPayload ? { context: event.requestPayload } : {}),
        recordedAt: event.recordedAt,
      }),
    };
  }

  /**
   * Create an impersonation end activity log request.
   *
   * @param impersonatedUserName - Name of the user who was impersonated
   * @param durationStr - Formatted duration string (e.g., "2 hours 15 mins")
   * @param activityEvents - Events recorded during the session (from `ActivityTrackerService.endSession()`)
   * @param additionalData - Additional data to include in newValues JSON
   */
  static createImpersonationEndLog(
    impersonatedUserName: string,
    durationStr: string,
    additionalData?: Record<string, unknown>,
    activityEvents?: ActivityEvent[]
  ): CreateActivityLogRequest {
    const activityDetails: ActivityLogDetail[] | undefined = activityEvents?.map((e) => ({
      stepName: e.stepName,
      stepDescription: e.stepDescription,
      status: e.status,
      sequenceNo: e.sequenceNo,
      startTime: e.recordedAt,
      endTime: e.recordedAt,
      durationMs: 0,
      requestPayload: e.requestPayload ?? null,
      responsePayload: e.responsePayload ?? null,
      errorMessage: e.errorMessage ?? null,
    }));

    return {
      eventTypeCode: ActivityEventType.SIMULATION,
      moduleName: ActivityModule.ADMIN,
      entityTypeName: ActivityEntityType.USER,
      actionTemplate: `{UserName} Impersonated ${impersonatedUserName} for ${durationStr}`,
      entityId: additionalData?.['impersonatedUserId'] as string | undefined,
      newValues: JSON.stringify({
        endTime: new Date().toISOString(),
        ...additionalData,
      }),
      ...(activityDetails?.length ? { activityDetails } : {}),
    };
  }
}
